import React from "react";
import styled from "styled-components";
import theme from "../../theme";

/** Shared Recharts styling so every chart in the app reads the same. */

export const axisProps = {
  stroke: theme.color.textFaint,
  fontSize: 12,
  tickLine: false,
  axisLine: false,
  tickMargin: 10,
};

export const gridProps = {
  vertical: false,
  stroke: theme.color.border,
  strokeDasharray: "3 3",
};

export const compactNumber = (value) => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return `${n}`;
};

/** <defs> gradients, one per series key, matching the series colour. */
export const SeriesGradients = ({ keys, idPrefix = "grad" }) => (
  <defs>
    {keys.map((key, index) => (
      <linearGradient
        key={key}
        id={`${idPrefix}-${key}`}
        x1="0"
        y1="0"
        x2="0"
        y2="1"
      >
        <stop
          offset="0%"
          stopColor={theme.chart[index % theme.chart.length]}
          stopOpacity={0.22}
        />
        <stop
          offset="100%"
          stopColor={theme.chart[index % theme.chart.length]}
          stopOpacity={0}
        />
      </linearGradient>
    ))}
  </defs>
);

const TooltipBox = styled.div`
  background: ${theme.color.surface};
  border: 1px solid ${theme.color.border};
  border-radius: ${theme.radius.md};
  box-shadow: ${theme.shadow.md};
  padding: 12px 14px;
  min-width: 150px;

  .title {
    font-size: ${theme.font.tiny};
    font-weight: 600;
    color: ${theme.color.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 8px;
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    font-size: ${theme.font.small};
    color: ${theme.color.text};

    & + .row {
      margin-top: 6px;
    }

    .name {
      display: flex;
      align-items: center;
      gap: 8px;
      color: ${theme.color.textMuted};
    }

    .swatch {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex: none;
    }

    strong {
      font-variant-numeric: tabular-nums;
      font-weight: 600;
    }
  }
`;

export const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;

  return (
    <TooltipBox>
      <div className="title">{label}</div>
      {payload.map((entry) => (
        <div className="row" key={entry.dataKey}>
          <span className="name">
            <span className="swatch" style={{ background: entry.color }} />
            {entry.name}
          </span>
          <strong>
            {formatter ? formatter(entry.value) : entry.value?.toLocaleString()}
          </strong>
        </div>
      ))}
    </TooltipBox>
  );
};

const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: ${theme.font.small};
    color: ${theme.color.textMuted};
  }

  i {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    display: inline-block;
  }
`;

export const ChartLegend = ({ keys }) => (
  <LegendRow>
    {keys.map((key, index) => (
      <span key={key}>
        <i style={{ background: theme.chart[index % theme.chart.length] }} />
        {key}
      </span>
    ))}
  </LegendRow>
);

export const ChartEmpty = styled.div`
  height: ${({ $height }) => $height || 320}px;
  display: grid;
  place-items: center;
  color: ${theme.color.textFaint};
  font-size: ${theme.font.small};
`;
